import React, { useState, useEffect } from "react";
import axios from "axios";
import { format, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useNavigate } from "react-router-dom";
import { FaMapMarkerAlt } from "react-icons/fa";
import { Marker, Popup, TileLayer } from "react-leaflet";
import Modal from "react-modal";
import L from "leaflet";

// Importações de componentes e utilitários internos
import * as S from "./styles";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import colaborador from "../../assets/collaborator.png";
import "leaflet/dist/leaflet.css";
import BuscaPonto from '../../services/ponto/BuscaPontoPordia';
import secureStorage from '../../utils/SecureStorage';

// Configuração do ícone padrão para marcadores do mapa
const defaultIcon = L.icon({
  iconUrl: "https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowUrl: "https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png",
  shadowSize: [41, 41],
});

// Componente de Menu Lateral
const SidebarMenu = ({ navigate, rotaAtiva }) => {
  const itensMenu = [
    { label: "Home", path: "/home", icone: null },
    { label: "Registro Ponto", path: "/Ponto", icone: null },
    { label: "Gestão Ponto", path: "/GestaoPonto", icone: null },
    { label: "Relatorio", path: "/Relatorio", icone: null },
  ];

  return (
    <>
      {itensMenu.map((item, indice) => (
        <S.MenuButton 
          key={indice} 
          onClick={() => navigate(item.path)}
          active={rotaAtiva === item.path}
        >
          {item.label}
        </S.MenuButton>
      ))}
    </>
  );
};

const AcompanhamentoPonto = () => {
  // Estados do componente
  const [visitas, setVisitas] = useState([]);
  const [selectedDate, setSelectedDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [currentAddress, setCurrentAddress] = useState("");
  
  // Hooks e serviços
  const navigate = useNavigate();
  const { loadregistros } = BuscaPonto([]);

  // Função para carregar registros de ponto
  const loadregistrosponto = async () => {
    const id_rede = secureStorage.getItem("id_r");
    const data = selectedDate;
  
    if (!id_rede || !data) {
      console.error("ID da Rede ou Data não fornecidos.");
      return;
    }
  
    console.log(`Buscando registros para id_rede: ${id_rede} e data: ${data}`);
    const error = await loadregistros(id_rede, data);
  
    if (error) {
      console.error("Erro ao carregar registros:", error);
      return;
    }
  
    const dataUser = secureStorage.getItem("dataponto");
    if (dataUser) {
      const registros = JSON.parse(dataUser);
      const colaboradores = registros.map((registro) => ({
        colaborador: registro.colaborador,
        foto: colaborador,
        data: registro.data,
        dia_da_semana: registro.dia_da_semana,
        batidas: registro.batidas || [],
      }));
  
      setVisitas(colaboradores);
      console.log("Dados carregados com sucesso:", colaboradores);
    } else {
      console.warn("Nenhum dado encontrado no storage.");
      setVisitas([]);
    }
  };
  
  // Efeito para carregar registros quando a data muda
  useEffect(() => {
    loadregistrosponto();
  }, [selectedDate]);

  // Busca o endereço de uma localização
  const fetchAddress = async (latitude, longitude) => {
    try {
      const response = await axios.get("https://nominatim.openstreetmap.org/reverse", {
        params: {
          lat: latitude,
          lon: longitude,
          format: "json",
        },
      });
      const data = response.data;
      setCurrentAddress(data.display_name || "Endereço não encontrado");
    } catch (error) {
      console.error("Erro ao buscar o endereço:", error);
      setCurrentAddress("Erro ao carregar endereço");
    }
  };

  // Abre o modal do mapa para uma localização
  const openMapModal = (location) => {
    if (location.latitude && location.longitude) {
      setCurrentLocation(location);
      fetchAddress(location.latitude, location.longitude);
      setModalIsOpen(true);
    } else {
      alert("Localização inválida. Não é possível abrir o mapa.");
    }
  };

  // Fecha o modal do mapa
  const closeMapModal = () => {
    setModalIsOpen(false);
    setCurrentLocation(null);
    setCurrentAddress("");
  };

  // Formata data no padrão brasileiro
  const formatDate = (dateStr) => {
    const date = parseISO(dateStr.split("T")[0]);
    return format(date, "dd/MM/yyyy", { locale: ptBR });
  };

  // Formata dia da semana
  const formatWeekDay = (dateStr) => {
    const date = parseISO(dateStr.split("T")[0]);
    return format(date, "EEEE", { locale: ptBR }).toUpperCase();
  };

  // Renderiza batidas em pares
  const renderBatidasInPairs = (batidas) => {
    const pairs = [];
    for (let i = 0; i < batidas.length; i += 2) {
      pairs.push(
        <S.RecordTimestampContainer key={i}>
          <div>
            {batidas[i] && (
              <>
                <S.RecordTimestamp>
                  {format(parseISO(batidas[i].timestamp), "HH:mm")}
                </S.RecordTimestamp>
                <S.MapMarkerIcon
                  onClick={() => openMapModal(batidas[i].localizacao)}
                />
              </>
            )}
          </div>
          <div>
            {batidas[i + 1] && (
              <>
                <S.RecordTimestamp>
                  {format(parseISO(batidas[i + 1].timestamp), "HH:mm")}
                </S.RecordTimestamp>
                <S.MapMarkerIcon
                  onClick={() => openMapModal(batidas[i + 1].localizacao)}
                />
              </>
            )}
          </div>
        </S.RecordTimestampContainer>
      );
    }
    return pairs;
  };

  // Renderização do componente
  return (
    <S.Wrapper>
      <S.Sidebar>
        <S.SidebarTitle>
          <FaMapMarkerAlt style={{ marginRight: '8px' }} />
          Ponto
        </S.SidebarTitle>
        <SidebarMenu navigate={navigate} rotaAtiva="/ponto/acompanhamento" />
      </S.Sidebar>

      <S.MainContent>
        <Header />
        
        <S.Title>
          <h1>Acompanhamento de Ponto</h1>
        </S.Title>

        <S.FilterArea>
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="input"
          />
        </S.FilterArea>

        <S.Content>
          {visitas.map((t, index) => (
            <S.VisitaCard key={`${t._id}-${index}`}>
              <S.AvatarPlaceholder>
                {t.colaborador.split(' ')[0].charAt(0)}
                {t.colaborador.split(' ')[1]?.charAt(0)}
              </S.AvatarPlaceholder>
              <S.VisitaCardTitle>{t.colaborador}</S.VisitaCardTitle>
              <S.VisitaCardParagraph>
                Data: {formatDate(t.data)} - {formatWeekDay(t.data)}
              </S.VisitaCardParagraph>
              <S.VisitaCardParagraph>Registros:</S.VisitaCardParagraph>
              {renderBatidasInPairs(t.batidas)}
            </S.VisitaCard>
          ))}
        </S.Content>

        <Modal
          isOpen={modalIsOpen}
          onRequestClose={closeMapModal}
          style={{
            overlay: {
              zIndex: 1000,
              backgroundColor: "rgba(0, 0, 0, 0.5)",
            },
            content: {
              top: "50%",
              left: "50%",
              right: "auto",
              bottom: "auto",
              marginRight: "-50%",
              transform: "translate(-50%, -50%)",
              width: "90%",
              height: "80%",
              padding: "0",
            },
          }}
        >
          {currentLocation && (
            <S.MapModalOverlay>
              <S.MapAddressDisplay>
                <strong>Endereço:</strong> {currentAddress}
              </S.MapAddressDisplay>
              <S.MapContainer
                center={[currentLocation.latitude, currentLocation.longitude]}
                zoom={16}
              >
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                <Marker
                  position={[currentLocation.latitude, currentLocation.longitude]}
                  icon={defaultIcon}
                >
                  <Popup>
                    Localização: {currentLocation.latitude}, {currentLocation.longitude}
                  </Popup>
                </Marker>
              </S.MapContainer>
            </S.MapModalOverlay>
          )}
          <S.CloseMapButton onClick={closeMapModal}>
            Fechar Mapa
          </S.CloseMapButton>
        </Modal>
      </S.MainContent>

      <Footer />
    </S.Wrapper>
  );
};

export default AcompanhamentoPonto;